import {
  Box,
  Button,
  Grid,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ErrorMessage, Form, Formik, FormikProps } from "formik";
import { useRef, useState } from "react";
import * as Yup from "yup";

interface HighImpactSettingProps {
  handleNext: any;
  handleBack: any;
}

interface HighImpactFormValues {
  username: string;
  password: string;
}

export const HighImpactValidationSchema = Yup.object().shape({
  username: Yup.string()
    .required("Username is required")
    .min(3, "Username must be at least 3 characters"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),
});

export const HighImpactSetting: React.FunctionComponent<
  HighImpactSettingProps
> = ({ handleNext, handleBack }) => {
  const formRef = useRef<FormikProps<any>>(null);
  const [connectionStatus, setConnectionStatus] = useState({
    isProcessed: false,
    isValid: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const handleConnection = async (values: HighImpactFormValues) => {
    setIsLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setConnectionStatus({ isProcessed: true, isValid: true });
    } catch (err: any) {
      setConnectionStatus({ isProcessed: true, isValid: false });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minHeight={"80vh"}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          p: 0.5,
          mt: 2,
        }}
      >
        <Button variant="outlined" size="small" onClick={handleBack}>
          Back
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleNext}
          disabled={!(connectionStatus.isProcessed && connectionStatus.isValid)}
        >
          Next
        </Button>
      </Box>

      <Grid container style={{ justifyContent: "center" }}>
        <Grid item xs={12} sm={6}>
          <Formik
            initialValues={{ username: "", password: "" }}
            enableReinitialize
            validationSchema={HighImpactValidationSchema}
            onSubmit={(values) => {
              handleConnection(values);
            }}
            innerRef={formRef}
          >
            {({ values, handleChange, handleBlur }) => (
              <Form>
                <FormControl
                  sx={{ m: 3 }}
                  component="fieldset"
                  variant="standard"
                  style={{ alignItems: "center" }}
                >
                  <FormLabel component="h2" sx={{ fontSize: "1.5rem", mb: 3 }}>
                    HiImpact Setup
                  </FormLabel>

                  <Grid container spacing={3} p={2}>
                    {/* Status Alert */}
                    <Grid item sm={12}>
                      {connectionStatus.isProcessed && (
                        <Alert
                          severity={
                            connectionStatus.isValid ? "success" : "error"
                          }
                        >
                          {connectionStatus.isValid
                            ? "Successfully connected to HiImpact!"
                            : "Failed to connect. Please check your credentials and try again."}
                        </Alert>
                      )}
                    </Grid>

                    {/* Form Fields */}
                    <Grid item sm={12}>
                      <TextField
                        id="username"
                        name="username"
                        label="Username"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.username}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="username" />}
                        error={
                          !!formRef.current?.errors?.username &&
                          !!formRef.current?.touched?.username
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <TextField
                        id="password"
                        name="password"
                        label="Password"
                        type="password"
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                        disabled={connectionStatus.isValid}
                        helperText={<ErrorMessage name="password" />}
                        error={
                          !!formRef.current?.errors?.password &&
                          !!formRef.current?.touched?.password
                        }
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <Button
                        variant="contained"
                        type="submit"
                        size="small"
                        disabled={isLoading || connectionStatus.isValid}
                        startIcon={
                          isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                      >
                        {isLoading ? "Connecting..." : "Connect to HiImpact"}
                      </Button>
                    </Grid>
                  </Grid>
                </FormControl>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HighImpactSetting;
